// "Link this barcode to…" (kitchen flow, Stage 2D): an unknown scan on any
// scanner screen picks the existing item it belongs to (one unit or one
// purchase unit per scan), saves it, and hands the match back so the scan
// carries on. Strings: lib/inventoryFlow/barcodeLinkStrings.ts.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { BarcodeMatch } from '@/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { BL } from '@/lib/inventoryFlow/barcodeLinkStrings'
import { packChoices } from '@/lib/inventoryFlow/barcodeLinkLogic'
import type { FlowItem } from '@/lib/inventoryFlow/types'

export function LinkBarcodeDialog({ code, onLinked, onClose }: { code: string | null; onLinked: (m: BarcodeMatch) => void; onClose: () => void }) {
  const [search, setSearch] = useState('')
  const [item, setItem] = useState<FlowItem | null>(null)
  const [pack, setPack] = useState<'one' | 'purchase' | 'other'>('one')
  const [other, setOther] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { data } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), enabled: Boolean(code), staleTime: 60_000 })
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return (data?.data ?? []).filter((i) => i.name.toLowerCase().includes(q) || (i.code ?? '').toLowerCase().includes(q)).slice(0, 12)
  }, [data, search])
  const choices = item ? packChoices(item) : []
  const qty = pack === 'other' ? Number(other) : choices.find((c) => c.key === pack)?.qty ?? 1
  const reset = () => { setSearch(''); setItem(null); setPack('one'); setOther(''); setError(null) }
  const close = () => { reset(); onClose() }
  const save = async () => {
    if (!code || !item || !(qty > 0)) return
    setBusy(true)
    setError(null)
    try {
      const label = pack === 'purchase' ? choices.find((c) => c.key === 'purchase')?.label : undefined
      const res = await apiClient.linkBarcode({ barcode: code, stock_item_id: item.id, pack_qty: qty, pack_label: label }, newIdempotencyKey())
      if (res.data) {
        reset()
        onLinked(res.data)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={Boolean(code)} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{BL.title}</DialogTitle>
          <DialogDescription>{BL.help(code ?? '')}</DialogDescription>
        </DialogHeader>
        {item ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-base font-semibold">{item.name}</span>
              <Button variant="ghost" className="h-10" onClick={() => setItem(null)}>{BL.change}</Button>
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{BL.perScan}</span>
              <div className="flex flex-wrap gap-2">
                {choices.map((c) => (
                  <button key={c.key} type="button" aria-pressed={pack === c.key} onClick={() => setPack(c.key)}
                    className={cn('h-10 rounded-lg border-2 px-4 font-semibold', pack === c.key ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}>
                    {c.key === 'one' ? BL.one(item.unit) : BL.purchase(c.label ?? '', String(c.qty), item.unit)}
                  </button>
                ))}
                <button type="button" aria-pressed={pack === 'other'} onClick={() => setPack('other')}
                  className={cn('h-10 rounded-lg border-2 px-4 font-semibold', pack === 'other' ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}>
                  {BL.other}
                </button>
                {pack === 'other' && <Input type="number" min={0} step="any" className="w-28" aria-label={BL.other} value={other} onChange={(e) => setOther(e.target.value)} />}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Input autoFocus placeholder={BL.search} value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="flex max-h-60 flex-wrap gap-2 overflow-y-auto">
              {search.trim() && matches.length === 0 && <p className="text-sm text-muted-foreground">{BL.noMatch}</p>}
              {matches.map((i) => (
                <button key={i.id} type="button" className="min-h-10 rounded-full border px-3 text-sm hover:bg-muted" onClick={() => setItem(i)}>
                  {i.name} <span className="text-muted-foreground">({i.unit})</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>{BL.cancel}</Button>
          <Button disabled={!item || !(qty > 0) || busy} onClick={() => void save()}>{busy ? BL.saving : BL.save}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
